// React
import * as React from "react"

// Libraries
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cva, type VariantProps } from "class-variance-authority"

// Icons
import { UserFallbackIcon } from "@/react/components/icons/UserFallbackIcon"

// Hooks and utilities
import { cn } from "@/lib/utils"

const avatarVariants = cva(
  "relative inline-flex items-center justify-center overflow-hidden rounded-full bg-dinnerplate-white text-grey shrink-0",
  {
    variants: {
      size: {
        sm: "size-avatar-sm",
        md: "size-avatar-md",
        lg: "size-avatar-lg",
        xl: "size-avatar-xl",
        "2xl": "size-avatar-2xl",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

export interface AvatarProps
  extends React.ComponentProps<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string
  /** Alt text for the image; falls back to `name`, then `""` (decorative). */
  alt?: string
  /** Display name — used only as the image alt fallback. The empty state is the silhouette icon, not initials. */
  name?: string
}

function Avatar({ className, size, src, alt, name, children, ...props }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cn(avatarVariants({ size }), className)}
      {...props}
    >
      <AvatarPrimitive.Image
        src={src}
        alt={alt ?? name ?? ""}
        className="size-full object-cover"
      />
      <AvatarPrimitive.Fallback className="flex size-full items-center justify-center">
        <UserFallbackIcon className="size-[35%]" />
      </AvatarPrimitive.Fallback>
      {children}
    </AvatarPrimitive.Root>
  )
}

export { Avatar, avatarVariants }
